import { Box, Button, type ButtonProps } from "@mui/material";
import { forwardRef, useId, type CSSProperties } from "react";

const aiGradientColors = [
  "#1976d2",
  "#A07CFE",
  "#FE8FB5",
  "#FFBE7B",
  "#1976d2",
];

const aiGradientTextStyle: CSSProperties = {
  background: `linear-gradient(90deg, ${aiGradientColors.join(", ")})`,
  backgroundSize: "200% 100%",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: "shine-right 5s infinite linear",
  fontSize: "0.8125rem",
  lineHeight: "1.75",
};

type AiGradientButtonProps = ButtonProps;

export const AiGradientButton = forwardRef<
  HTMLButtonElement,
  AiGradientButtonProps
>(({ children, startIcon, variant = "text", ...props }, ref) => {
  const gradientId = useId();

  return (
    <Button
      ref={ref}
      variant={variant}
      {...props}
      startIcon={
        startIcon && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              "& > svg": { fill: `url(#${gradientId})` },
            }}
          >
            <svg
              width="0"
              height="0"
              aria-hidden="true"
              focusable="false"
              className="absolute"
            >
              <defs>
                <linearGradient
                  id={gradientId}
                  x1="-2"
                  x2="0"
                  spreadMethod="repeat"
                >
                  <animate
                    attributeName="x1"
                    values="-2;2"
                    dur="5s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="x2"
                    values="0;4"
                    dur="5s"
                    repeatCount="indefinite"
                  />
                  {aiGradientColors.map((color, index) => (
                    <stop
                      key={index}
                      offset={`${index * 25}%`}
                      stopColor={color}
                    />
                  ))}
                </linearGradient>
              </defs>
            </svg>
            {startIcon}
          </Box>
        )
      }
    >
      <span style={aiGradientTextStyle}>{children}</span>
    </Button>
  );
});

AiGradientButton.displayName = "AiGradientButton";
